import { CardRowProps } from '@uma/react-commons/dist/components/Card/CardWrapper/CardRow';
import {
  getCountryMappings,
  getHpIdCountry,
  ROW_TYPE,
  AllCountriesDataProps,
  Loading
} from '@uma/react-commons/dist/index';
import {
  IconPolicies,
  IconPrinter,
  IconShoppingCart,
  IconSupport
} from '@veneer/core';
import React, { useState, useEffect } from 'react';
import { useDependencyManagerContext } from 'src/contexts/dependencyManager';
import { URLS, getHPSmartPrinterOverviewUrl } from 'src/utils/urls';
import {
  DeviceRegisterButtonClicked,
  DevicesMyPrintersButtonClicked,
  GetSupportForDeviceNotShownButtonClicked,
  SeeDevicesPurchasedButtonClicked
} from './analytics';
import { DoNotSeeDeviceContainer } from './styles';

export const DoNotSeeDevice = ({
  seeDevicesPurchasedOnlyForUS,
  isLoading,
  showControlHubLabel
}: {
  seeDevicesPurchasedOnlyForUS: boolean;
  isLoading: boolean;
  showControlHubLabel: boolean;
}): React.JSX.Element => {
  const { translate } = useDependencyManagerContext();
  const [isUserFromUs, setIsUserFromUs] = useState<boolean>(false);
  const [seeDevicesPurchasedLink, setSeeDevicesPurchasedLink] =
    useState<string>(URLS.DevicesPurchased);

  useEffect(() => {
    const fetchUserHpIdCountry = async () => {
      const userCountry = await getHpIdCountry();
      if (userCountry === 'US') {
        setIsUserFromUs(true);
      }
    };
    const fetchLink = async () => {
      const link = await getCountryMappings(
        AllCountriesDataProps.seeDevicesPurchasedLink
      );
      if (typeof link === 'string') {
        setSeeDevicesPurchasedLink(link);
      }
    };
    fetchUserHpIdCountry();
    fetchLink();
  }, []);

  const rows: CardRowProps[] = [];
  let currentRowIndex = 0;

  if (isLoading) {
    rows.push({
      index: currentRowIndex++,
      content: <Loading testid="do-not-see-device-loading" />
    });
  }

  if ((!seeDevicesPurchasedOnlyForUS || isUserFromUs) && !isLoading) {
    rows.push({
      index: currentRowIndex++,
      content: translate(
        'devices.seeDevice',
        `See devices you've purchased on HP.com`
      ) as string,
      icon: <IconShoppingCart filled={false} />,
      link: {
        dataTestId: 'DevicesPurchased',
        url: seeDevicesPurchasedLink
      },
      type: ROW_TYPE.EXTERNAL,
      analyticsEvent: SeeDevicesPurchasedButtonClicked(seeDevicesPurchasedLink)
    });
  }

  rows.push(
    {
      index: currentRowIndex++,
      content: translate(
        'devices.getSupportForDeviceNotShown',
        `Get support for a device that's not shown above`
      ) as string,
      icon: <IconSupport filled={false} />,
      link: {
        dataTestId: 'DeviceSupportNotShown',
        url: URLS.DeviceSupportNotShown
      },
      type: ROW_TYPE.EXTERNAL,
      analyticsEvent: GetSupportForDeviceNotShownButtonClicked
    },
    {
      index: currentRowIndex++,
      content: showControlHubLabel
        ? (translate(
            'devices.viewOrAddPrintersControlHub',
            'View or add a printer on HP Control Hub'
          ) as string)
        : (translate(
            'devices.viewOrAddPrinters',
            'View or add a printer on HP Smart'
          ) as string),
      icon: <IconPrinter filled={false} />,
      link: {
        dataTestId: 'HPSmartDevicesMyPrinters',
        url: getHPSmartPrinterOverviewUrl()
      },
      type: ROW_TYPE.EXTERNAL,
      analyticsEvent: DevicesMyPrintersButtonClicked(showControlHubLabel)
    },
    {
      index: currentRowIndex++,
      content: translate(
        'devices.registerDevice',
        `Register a device`
      ) as string,
      icon: <IconPolicies filled={false} />,
      link: {
        dataTestId: 'Register',
        url: URLS.Register
      },
      type: ROW_TYPE.EXTERNAL,
      analyticsEvent: DeviceRegisterButtonClicked
    }
  );

  return (
    <DoNotSeeDeviceContainer
      title={{
        content: translate(
          'devices.dontSeeDevice',
          `Don't see the device you're looking for?`
        ) as string
      }}
      rows={rows}
    />
  );
};
